import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";
import { Theme } from "@radix-ui/themes";
import { useTheme } from "~/routes/resources/theme-switch";

interface DemoThemeContextType {
  primaryColor: string;
  setPrimaryColor: (color: string) => void;
  appearance: "light" | "dark";
  setAppearance: (appearance: "light" | "dark") => void;
}

const DemoThemeContext = createContext<DemoThemeContextType | undefined>(
  undefined
);

export const useDemoTheme = () => {
  const context = useContext(DemoThemeContext);
  if (!context) {
    throw new Error("useDemoTheme must be used within a DemoThemeProvider");
  }
  return context;
};

interface CustomColors {
  primary?: string;
  secondary?: string;
  tertiary1?: string;
  tertiary2?: string;
  background?: string;
  border?: string;
  green?: string;
  red?: string;
  yellow?: string;
  surface?: string;
}

interface DemoThemeProviderProps {
  children: ReactNode;
  initialPrimaryColor?: string;
  customColors?: CustomColors;
  initialAppearance?: "light" | "dark";
}

export function DemoThemeProvider({
  children,
  initialPrimaryColor = "#4095A8",
  customColors,
  initialAppearance,
}: DemoThemeProviderProps) {
  const [primaryColor, setPrimaryColor] = useState(initialPrimaryColor);
  const systemTheme = useTheme();
  const [manualOverride, setManualOverride] = useState<"light" | "dark" | null>(
    initialAppearance || null
  );

  // Sync primaryColor state when initialPrimaryColor prop changes
  useEffect(() => {
    setPrimaryColor(initialPrimaryColor);
  }, [initialPrimaryColor]);

  const appearance =
    manualOverride || (systemTheme as "light" | "dark") || "light";
  const setAppearance = (theme: "light" | "dark") => setManualOverride(theme);

  const themeValue: DemoThemeContextType = {
    primaryColor,
    setPrimaryColor,
    appearance,
    setAppearance,
  };

  // Convert hex to RGB
  const hexToRgb = (hex: string) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result
      ? {
          r: parseInt(result[1], 16),
          g: parseInt(result[2], 16),
          b: parseInt(result[3], 16),
        }
      : { r: 64, g: 149, b: 168 };
  };

  const rgb = hexToRgb(primaryColor);

  // Merge custom colors with defaults
  const colors = {
    primary: customColors?.primary,
    secondary: customColors?.secondary,
    tertiary1: customColors?.tertiary1,
    tertiary2: customColors?.tertiary2,
    background: customColors?.background,
    border: customColors?.border,
    green: customColors?.green,
    red: customColors?.red,
    blue: customColors?.blue,
    yellow: customColors?.yellow,
    surface: customColors?.surface,
  };

  // Build custom style object with all colors
  const customStyle: Record<string, string> = {
    "--demo-primary-color": colors.primary ?? primaryColor,
    "--demo-primary-rgb": `${rgb.r}, ${rgb.g}, ${rgb.b}`,
    "--demo-primary-light": `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.1)`,
    "--demo-primary-medium": `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.2)`,
    "--demo-grid-gap": "24px",
    "--demo-border-radius-small": "8px",
    "--demo-border-radius-medium": "16px",
    "--demo-border-radius-large": "24px",
    "--accent-9": colors.primary ?? primaryColor,
    "--accent-10": `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.9)`,
    "--accent-11": `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.8)`,
    "--accent-a9": `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.8)`,
    "--demo-grid-mobile": "1fr",
    "--demo-grid-tablet": "1fr 1fr",
    "--demo-grid-desktop": "2fr 1fr",
    "--demo-secondary-color": colors.secondary ?? "#5B8DEF",
    "--demo-tertiary1-color": colors.tertiary1 ?? "#7BA5D8",
    "--demo-tertiary2-color": colors.tertiary2 ?? "#C4E3F7",
    "--demo-background-color": colors.background ?? "#F8FAFB",
    "--demo-border-color": colors.border ?? "#E0E0E0",
    "--demo-green-color": colors.green ?? "#4CAF50",
    "--demo-red-color": colors.red ?? "#F44336",
    "--demo-yellow-color": colors.yellow ?? "#FF9800",
    "--demo-surface-color": colors.surface ?? "#FFFFFF",
    "--demo-blue-color": colors.blue ?? "#4095A8",

    // Override Radix UI color variables
    "--blue-9": colors.blue ?? primaryColor,
    "--blue-10": colors.blue ?? primaryColor,
    "--blue-11": colors.blue ?? primaryColor,
    "--blue-a9": colors.blue ?? primaryColor,

    "--green-3": `${colors.green ?? "#4CAF50"}20`,
    "--green-9": colors.green ?? "#4CAF50",
    "--green-10": colors.green ?? "#4CAF50",
    "--green-11": colors.green ?? "#4CAF50",

    "--red-3": `${colors.red ?? "#F44336"}20`,
    "--red-9": colors.red ?? "#F44336",
    "--red-10": colors.red ?? "#F44336",
    "--red-11": colors.red ?? "#F44336",
  };

  return (
    <DemoThemeContext.Provider value={themeValue}>
      <Theme
        appearance={appearance}
        accentColor="blue"
        radius="full"
        style={customStyle as React.CSSProperties}
      >
        <style
          dangerouslySetInnerHTML={{
            __html: `
              .demo-grid {
                display: grid;
                gap: var(--demo-grid-gap);
                grid-template-columns: var(--demo-grid-mobile);
              }

              @media (min-width: 768px) {
                .demo-grid {
                  grid-template-columns: var(--demo-grid-tablet);
                }
              }

              @media (min-width: 1024px) {
                .demo-grid {
                  grid-template-columns: var(--demo-grid-desktop);
                }
              }

              .demo-card {
                background: var(--color-surface);
                border: 1px solid var(--color-border);
                border-radius: var(--demo-border-radius-medium);
                box-shadow: var(--shadow-2);
              }

              .demo-primary-button {
                background-color: var(--demo-primary-color) !important;
                color: white !important;
              }

              .demo-primary-button:hover {
                background-color: rgba(var(--demo-primary-rgb), 0.9) !important;
              }

              .demo-outline-button {
                border-color: var(--demo-primary-color);
                color: var(--demo-primary-color);
              }

              .demo-outline-button:hover {
                background-color: var(--demo-primary-light);
              }

              /* Prevent page jumping on button clicks */
              .demo-no-jump {
                scroll-behavior: auto !important;
              }

              .demo-prevent-scroll {
                position: relative;
                overflow-anchor: none;
              }

              /* Panel styles */
              .demo-collapsible-panel {
                position: relative;
                overflow: visible;
              }

              .demo-panel-content {
                overflow-y: auto;
                overflow-x: visible;
              }

              /* Custom scrollbar for panel */
              .demo-panel-content::-webkit-scrollbar {
                width: 6px;
              }

              .demo-panel-content::-webkit-scrollbar-track {
                background: var(--color-surface);
              }

              .demo-panel-content::-webkit-scrollbar-thumb {
                background: var(--color-border);
                border-radius: 3px;
              }

              .demo-panel-content::-webkit-scrollbar-thumb:hover {
                background: var(--color-border-hover);
              }

              /* Mobile view styles */
              .phone-mockup {
                background: linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%);
              }

              .mobile-card {
                background: var(--color-surface);
                border: 1px solid var(--gray-4);
                border-radius: 12px;
                padding: 8px;
                box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
                width: 100%;
                max-width: 100%;
                overflow: hidden;
                box-sizing: border-box;
              }

              .mobile-card:hover {
                box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
                transition: box-shadow 0.2s ease;
              }

              /* Override fixed widths in mobile view */
              .mobile-card .rt-Card,
              .mobile-card > div {
                width: 100% !important;
                max-width: 100% !important;
                min-width: 0 !important;
              }
            `,
          }}
        />
        {children}
      </Theme>
    </DemoThemeContext.Provider>
  );
}
